import React, { useState, useEffect } from 'react';
import { createAppUser } from '../../services/authService';
import Header from '../../components/Header';
import axios from 'axios';
import * as XLSX from 'xlsx';

const LoadingSpinner = () => (
  <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
    <div className="w-16 h-16 border-t-4 border-white border-solid rounded-full animate-spin"></div>
  </div>
);

const AddAppUser = () => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [group, setGroup] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [isBulkUpload, setIsBulkUpload] = useState(false);
  const [invalidEmails, setInvalidEmails] = useState([]); // Step 1: Track invalid emails

  useEffect(() => {
    const timer = setTimeout(() => {
      setError('');
      setSuccess('');
    }, 10000);
    return () => clearTimeout(timer);
  }, [error, success]);

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const sendEmail = async (email, newUser) => {
    const emailPayload = {
      email,
      subject: "Your Challenge App Temporary Admin Password",
      content: `
      <html>
    <body style="font-family: Arial, sans-serif; line-height: 1.6;">
    <div style="max-width: 600px; margin: auto; padding: 20px; border: 1px solid #ddd; border-radius: 5px; background-color: #f9f9f9;">
    <h2 style="color: #333;">Welcome to Challenge App ${firstName}!</h2>
      
      ${newUser
          ? `
        <p style="color: #555;">
  You have been selected to participate in the Fordham 30-Day Challenge!
</p>
<p style="color: #555;">
  To get started, please install the <em>Fordham Challenge</em> app from your app store. Once installed, you'll be ready to dive in and begin the challenge.
</p>
<p style="color: #555;">
  Thank you for joining us—let’s make these next 30 days impactful!
</p>
        `
          : `
        <p style="color: #555;">
          Welcome to Challenge App! You can now continue using the app with the same password you use for the Challenge App Admin Portal.
        </p>
        `}
        
      <p style="color: #555;">
        If you need assistance, please reach out to our support team at any time.
      </p>
      <p style="color: #555;">Thank you for being part of Challenge App!</p>
      <footer style="margin-top: 20px; font-size: 12px; color: #888;">
        <p>This email was sent to you by Challenge App.</p>
      </footer>
    </div>
  </body>
</html>
    `,
    };

    const formData = new FormData();
    formData.append('email', emailPayload.email);
    formData.append('subject', emailPayload.subject);
    formData.append('content', emailPayload.content);

    try {
      const response = await axios.post(
        "https://mail-service-191057255433.us-central1.run.app/api/Mail/sendmail",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return { success: true };
    } catch (error) {
      console.error("Error sending email:", error);
      return { success: false, error: error.message };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (!['A', 'B', 'C'].includes(group.toUpperCase())) {
      setError('Group must be A, B, or C.');
      setLoading(false);
      return;
    }

    if (!isValidEmail(email)) {
      setError('Please enter a valid email address.');
      setLoading(false);
      return;
    }

    try {
      const result = await createAppUser(email, firstName, lastName, group);
      if (result.success) {
        const emailResult = await sendEmail(email, result.newUser);
        if (emailResult.success) {
        setSuccess('User created successfully and email sent.');
        } else {
          setError(`User created, but email sending failed: ${emailResult.error}`);
        }
        setEmail('');
        setFirstName('');
        setLastName('');
        setGroup('');
      } else {
        setError(`Error: ${result.message}`);
      }
    } catch (error) {
      setError(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  const handleUpload = () => {
    if (!file) {
      setError("Please select a file before uploading.");
      return;
    }

    // Step 1: Validate file type
    const validMimeTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel"
    ];
    const validExtensions = ["xlsx", "xls"];

    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (!validMimeTypes.includes(file.type) || !validExtensions.includes(fileExtension)) {
      setError("Invalid file type. Please upload an Excel file (.xlsx or .xls).");
      return;
    }

    setLoading(true);
    const reader = new FileReader();
    reader.onload = async (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const users = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

      const headers = users[0];
      const userData = users.slice(1);
      const newInvalidEmails = [];

      for (let i = 0; i < userData.length; i++) {
        const user = userData[i];
        const email = user[headers.indexOf('Email')]?.trim();
        const firstName = user[headers.indexOf('First Name')]?.trim();
        const lastName = user[headers.indexOf('Last Name')]?.trim();
        const group = user[headers.indexOf('Group')]?.trim().toUpperCase();

        if (!email || !isValidEmail(email)) {
          newInvalidEmails.push({ email: email || "N/A", error: `Invalid or missing email format on row ${i + 2}.` });
          continue;
        }
        if (!firstName) {
          newInvalidEmails.push({ email, error: `Missing first name on row ${i + 2}.` });
          continue;
        }
        if (!lastName) {
          newInvalidEmails.push({ email, error: `Missing last name on row ${i + 2}.` });
          continue;
        }
        if (!['A', 'B', 'C'].includes(group)) {
          newInvalidEmails.push({ email, error: `Invalid group on row ${i + 2}. Must be A, B, or C.` });
          continue;
        }

        try {
          const result = await createAppUser(email, firstName, lastName, group);
          if (result.success) {
            await sendEmail(email, result.newUser);
            console.log("sendEmail Sucsess");
          } else {
            newInvalidEmails.push({ email, error: `Error Creating User: ${result.message}` });
          }
        } catch (error) {
          newInvalidEmails.push({ email, error: `Error creating user: ${error.message}` });
        }
      }

      setInvalidEmails(newInvalidEmails);
      setSuccess('Bulk user creation completed.');
      setLoading(false);
      setFile(null);
    };

    reader.onerror = (error) => {
      setError(`File reading error: ${error.message}`);
      setLoading(false);
      setFile(null);
    };

    reader.readAsArrayBuffer(file);
  };


  const downloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([
      ['Email', 'First Name', 'Last Name', 'Group'],
    ]);
    XLSX.utils.book_append_sheet(wb, ws, 'User Template');
    XLSX.writeFile(wb, 'User_Template.xlsx');
  };

  const handleBulkUploadClick = () => {
    setIsBulkUpload(true);
  };

  return (
    <div
      className=" flex items-center justify-center px-4 py-12"
    >
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl p-10">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
          Add New App User
        </h2>
          <div className="flex justify-center mb-6">
            <button
              onClick={() => setIsBulkUpload(false)}
              className={`px-4 py-2 ${!isBulkUpload ? "bg-[#900028] text-white" : "bg-gray-200 text-gray-700"} rounded-l-md`}
            >
              Single User
            </button>
            <button
              onClick={handleBulkUploadClick}
              className={`px-4 py-2 ${isBulkUpload ? "bg-[#900028] text-white" : "bg-gray-200 text-gray-700"} rounded-r-md`}
            >
              Bulk Upload
            </button>
          </div>

          {!isBulkUpload ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-gray-700">Email:</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full border border-gray-300 rounded-md p-2"
                />
              </div>
              <div>
                <label className="block text-gray-700">First Name:</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  className="w-full border border-gray-300 rounded-md p-2"
                />
              </div>
              <div>
                <label className="block text-gray-700">Last Name:</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  className="w-full border border-gray-300 rounded-md p-2"
                />
              </div>
              <div>
                <label className="block text-gray-700">Group:</label>
                <select
                  value={group}
                  onChange={(e) => setGroup(e.target.value)}
                  required
                  className="w-full border border-gray-300 rounded-md p-2"
                >
                  <option value="">Select a group</option>
                  <option value="A">A</option>
                  <option value="B">B</option>
                  <option value="C">C</option>
                </select>
              </div>
              <button
                type="submit"
                className="w-full bg-[#900028] text-white py-2 px-4 rounded-md shadow-sm"
              >
                Add AppUser
              </button>
            </form>
          ) : (
            <div className="space-y-4">
              <input type="file" onChange={handleFileChange} />
              <button
                onClick={handleUpload}
                className="w-full bg-[#900028] text-white py-2 px-4 rounded-md shadow-sm"
              >
                Upload File
              </button>
              <button
                onClick={downloadTemplate}
                className="mt-2 bg-[#FFCCEA] text-gray-700 py-1 px-1.5 rounded-md shadow-sm text-sm"
              >
                Download Template
              </button>
            </div>
          )}

          {error && <p className="mt-4 text-center text-red-500">{error}</p>}
          {success && <p className="mt-4 text-center text-green-500">{success}</p>}

          {/* Step 3: Render invalid emails in a table */}
          {invalidEmails.length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold">Invalid Emails</h3>
              <table className="min-w-full bg-white border border-gray-300">
                <thead>
                  <tr>
                    <th className="border px-4 py-2">Email</th>
                    <th className="border px-4 py-2">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {invalidEmails.map((entry, index) => (
                    <tr key={index}>
                      <td className="border px-4 py-2">{entry.email}</td>
                      <td className="border px-4 py-2">{entry.error}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {loading && <LoadingSpinner />}
    </div>
  );
};

export default AddAppUser;




